import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapDownIcon } from '~/assets/icons';
import type { IPersonStatus } from '~/utils/interface/map';
import type { TrackingDevice } from '../mapTrackingTypes';
import { getStatusLabel } from '../statusPerson';

export type MapStatusFilterValue = 'all' | number;

interface MapStatusFilterProps {
  value: MapStatusFilterValue;
  onFilterChange: (value: MapStatusFilterValue) => void;
  personStatuses: IPersonStatus[];
  devicesForStatusFilterCounts?: TrackingDevice[];
}

const PERSON_STATUS_VALUES = [0, 1, 2, 3, 4, 5];

function hexToRgba(hex: string, alpha: number): string {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const STATUS_FILTER_UI_COLORS: Record<number, string> = {
  0: '#22C55E',
  1: '#9CA3AF',
  2: '#F97316',
  3: '#EAB308',
  4: '#EF4444',
  5: '#7C3AED',
};

const ALL_ROW_BG_HIGHLIGHT = '#123B6D1A';

function MapStatusFilter({
  value,
  onFilterChange,
  personStatuses,
  devicesForStatusFilterCounts = [],
}: MapStatusFilterProps) {
  const { t, i18n } = useTranslation('admin/map');
  const [open, setOpen] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<MapStatusFilterValue | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const statusCounts = useMemo(() => {
    const byStatus = Object.fromEntries(PERSON_STATUS_VALUES.map((v) => [v, 0])) as Record<number, number>;
    for (const device of devicesForStatusFilterCounts) {
      const s = device.status;
      if (s !== null && PERSON_STATUS_VALUES.includes(s)) {
        byStatus[s]++;
      }
    }
    return {
      all: devicesForStatusFilterCounts.length,
      byStatus,
    };
  }, [devicesForStatusFilterCounts]);

  const options = useMemo(() => {
    return [
      { label: t('statusFilter.all'), value: 'all' as const, count: statusCounts.all },
      ...PERSON_STATUS_VALUES.map((statusValue) => ({
        value: statusValue,
        label: getStatusLabel(
          statusValue,
          personStatuses,
          t('statusFilter.statusFallback', { value: statusValue }),
          i18n.language
        ),
        count: statusCounts.byStatus[statusValue],
      })),
    ];
  }, [personStatuses, statusCounts, t, i18n.language]);

  const selectedOption = useMemo(() => {
    const found = options.find((option) => option.value === value);
    if (found) return found;
    return { label: t('statusFilter.all'), count: statusCounts.all };
  }, [options, statusCounts.all, t, value]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (!open) setHoveredRow(null);
  }, [open]);

  return (
    <div ref={wrapperRef} className='min-w-[220px] max-w-[min(100vw-2rem,320px)] w-max'>
      <label className='block text-xs font-semibold text-[#334155] mb-1.5 pl-1'>{t('statusFilter.label')}</label>
      <div className='relative'>
        <button
          type='button'
          onClick={() => setOpen((prev) => !prev)}
          className='flex h-10 w-full items-center rounded-xl border border-[#D8E0EA] bg-white px-3 pr-9 text-left text-sm font-normal text-[#0F172A] shadow-[0_4px_14px_rgba(15,23,42,0.08)] transition-colors duration-150 hover:bg-[#F8FAFC] hover:border-[#CBD5E1] focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400'
        >
          <span className='min-w-0 truncate'>
            {selectedOption.label}{' '}
            <span className='tabular-nums'>
              (<span className='relative top-px inline-block'>{selectedOption.count}</span>)
            </span>
          </span>
        </button>
        <span className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2'>
          <MapDownIcon className='h-[14px] w-[14px] opacity-70' />
        </span>

        {open && (
          <div
            className='absolute left-0 right-0 mt-2 rounded-xl border border-[#E2E8F0] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.12)] p-1 z-30'
            onMouseLeave={() => setHoveredRow(null)}
          >
            {options.map((option) => {
              const isSelected = option.value === value;
              const isHovered = hoveredRow === option.value;
              const showRowHighlight = hoveredRow !== null ? isHovered : isSelected;

              const backgroundColor =
                option.value === 'all'
                  ? showRowHighlight
                    ? ALL_ROW_BG_HIGHLIGHT
                    : 'transparent'
                  : showRowHighlight
                    ? hexToRgba(STATUS_FILTER_UI_COLORS[option.value], 0.2)
                    : 'transparent';

              const dotColor = option.value === 'all' ? undefined : STATUS_FILTER_UI_COLORS[option.value];

              return (
                <button
                  key={option.value}
                  type='button'
                  onMouseEnter={() => setHoveredRow(option.value)}
                  onClick={() => {
                    onFilterChange(option.value);
                    setOpen(false);
                  }}
                  style={{ backgroundColor }}
                  className='flex w-full items-center gap-2 rounded-lg px-2 py-2.5 text-left text-sm transition-colors duration-150'
                >
                  {dotColor !== undefined ? (
                    <span className='h-2 w-2 shrink-0 rounded-full' style={{ backgroundColor: dotColor }} aria-hidden />
                  ) : (
                    <span className='h-2 w-2 shrink-0' aria-hidden />
                  )}
                  <span className={showRowHighlight ? 'font-medium text-[#0F172A]' : 'font-normal text-[#94A3B8]'}>
                    {option.label}{' '}
                    <span className='tabular-nums'>
                      (<span className='relative top-px inline-block'>{option.count}</span>)
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default MapStatusFilter;
